import type de from './de';

/** English texts. Same keys as de.ts (checked by the type). */
const en: typeof de = {
  meta: {
    home: 'Temporary email address',
    login: 'Log in',
    mail: 'Inbox',
    faq: 'FAQ',
    blacklist: 'Blacklist check',
    imprint: 'Imprint',
  },
  nav: {
    create: 'New mailbox',
    login: 'Log in',
    mail: 'Inbox',
    faq: 'FAQ',
    blacklist: 'Blacklist',
    imprint: 'Imprint',
    language: 'Language',
    menu: 'Menu',
  },
  common: {
    loading: 'Loading …',
    cancel: 'Cancel',
    save: 'Save',
    days: '{n} day | {n} days',
    address: 'Email address',
    accountName: 'Account name',
    password: 'Password',
    passwordRepeat: 'Repeat password',
    lifetime: 'Lifetime',
    validUntil: 'valid until {date}',
  },
  home: {
    title: 'A temporary email address in seconds',
    intro:
      'Protect your private address: with a disposable mailbox from anonmails.de you can sign up for services, newsletters or tests without filling your real inbox with spam.',
    features: [
      'Lifetime of 1 to 50 days, extendable at any time',
      'Read in the browser or via POP3/POP3S',
      'No personal data required',
    ],
    donateText: 'Do you like anonmails.de? The service is free – a donation helps to keep it running.',
    donate: 'Donate with PayPal',
    loggedIn: 'You are logged in as {email}.',
    toMailbox: 'Go to inbox',
  },
  create: {
    title: 'Create a new mailbox',
    wish: 'Desired address',
    checking: 'Checking …',
    available: '{email} is available.',
    taken: 'This address is already taken.',
    reserved: 'This address is reserved.',
    invalid: 'Allowed are {min}–{max} characters: a–z, 0–9, dot, hyphen, underscore.',
    lifetimeHint:
      'Afterwards all messages are deleted. The address stays yours and can be reactivated with the last password.',
    submit: 'Create mailbox',
    busy: 'Creating …',
    terms: 'By creating a mailbox you accept the terms of use in the {link}.',
    termsLink: 'imprint',
  },
  login: {
    title: 'Log in',
    tabLogin: 'Log in',
    tabReactivate: 'Reactivate',
    submit: 'Log in',
    busy: 'Logging in …',
    noAccount: 'No mailbox yet?',
    expiredNotice: 'Your mailbox has expired. You can reactivate it with your last password.',
    lastPassword: 'Last password',
    reactivateHint: 'The mailbox is activated from today for the selected lifetime. Earlier messages have been deleted.',
    reactivateSubmit: 'Reactivate',
    reactivateBusy: 'Reactivating …',
    addressHint: 'The account name is enough, the domain is added automatically.',
  },
  password: {
    min: 'At least {min} characters.',
    mismatch: 'The passwords do not match.',
    title: 'Change password',
    current: 'Current password',
    new: 'New password',
    repeat: 'Repeat new password',
    hint: 'The new password applies immediately to POP3 clients as well.',
    changed: 'Password changed',
    change: 'Change password',
  },
  extend: {
    button: 'Extend',
    title: 'Extend mailbox',
    current: 'Valid until {date}.',
    rule: 'The new expiry date can be at most {max} days from today.',
    rule0: 'The mailbox already has the maximum lifetime of {max} days from today.',
    newDate: 'New expiry date: {date}',
    submit: 'Extend',
    busy: 'Extending …',
    done: 'Extended until {date}',
  },
  mail: {
    logout: 'Log out',
    folders: {
      inbox: 'Inbox',
      drafts: 'Drafts',
      sent: 'Sent',
      junk: 'Spam',
      trash: 'Trash',
      archive: 'Archive',
    },
    messages: '{n} message | {n} messages',
    empty: 'No messages',
    noSubject: '(no subject)',
    unknown: '(unknown)',
    attachment: 'Attachment',
    flag: 'Flag',
    unflag: 'Remove flag',
    from: 'From:',
    to: 'To:',
    cc: 'Cc:',
    download: 'Download (.eml)',
    downloadTitle: 'Save the original message as an .eml file',
    markUnread: 'Mark as unread',
    remoteBlocked: 'External content was blocked to protect your privacy.',
    loadRemote: 'Load anyway',
    noneSelected: 'No message selected',
  },
  manage: {
    select: 'Select',
    selectAll: 'Select all on this page',
    selected: '{n} selected',
    saveSelected: 'Save (ZIP)',
    saveSelectedTitle: 'Download the selected messages as a ZIP with .eml files',
    deleteSelected: 'Delete',
    delete: 'Delete',
    deleteOne: 'Delete message',
    confirmDelete: 'Delete this message permanently? | Delete {n} messages permanently?',
    emptyFolder: 'Empty {folder}',
    confirmEmpty: 'Permanently delete all messages in “{folder}”? This cannot be undone.',
    deleted: '{n} message deleted | {n} messages deleted',
    usage: 'Mailbox: {used} of {limit}',
    usageOnly: 'Mailbox: {used}',
    settings: 'Settings',
    settingsTitle: 'Settings',
    accountInfo: 'Mailbox',
    createdAt: 'Created on',
    expiresAt: 'Valid until',
    remaining: 'Remaining',
    remainingDays: '{n} day left | {n} days left',
    expired: 'expired',
    remoteImages: 'Always load external images in HTML emails',
    remoteImagesHint: 'External images tell the sender that and when you opened the email (tracking). Default: blocked, show them per message with “Load anyway”.',
    saved: 'Settings saved',
    deleteAccount: 'Delete account',
    deleteAccountTitle: 'Delete account',
    deleteAccountText: 'All messages are deleted permanently right away and you will be logged out. The address {email} stays yours and can be reactivated later with the last password.',
    deleteAccountConfirm: 'Yes, delete all messages and close the account',
    deleteAccountSubmit: 'Delete account',
    deleteAccountBusy: 'Deleting …',
    accountDeleted: 'Your account has been deleted. You can activate the address again under “Log in” → “Reactivate”.',
  },
  faq: {
    or: 'or',
    title: 'Frequently asked questions',
    items: [
      {
        q: 'What is anonmails.de?',
        a: 'anonmails.de provides free, temporary email mailboxes. Use them to protect your private address, for example when signing up for services, newsletters or for testing, and keep your real inbox free of spam.',
      },
      {
        q: 'How long is a mailbox valid?',
        a: 'When creating it you choose a lifetime of {options} days. After logging in, the expiry date is shown at the top of the inbox.',
      },
      {
        q: 'Can I extend the lifetime?',
        a: 'Yes. After logging in you can extend the mailbox with “Extend” at any time, up to {max} days from today. The remaining lifetime counts towards this limit.',
      },
      {
        q: 'What happens when a mailbox expires?',
        a: 'All messages are deleted. The address stays yours, though: under “Log in” → “Reactivate” you can activate the mailbox again with the last password you used.',
      },
      {
        q: 'Can I send emails with anonmails.de?',
        a: 'No. anonmails.de is not an email provider: messages can only be received, read and saved. Please use another (free) provider to send emails.',
      },
      {
        q: 'Is there a size limit?',
        a: 'Yes. A single email may be at most {maxMessage} MB, larger messages are rejected. A mailbox holds {quota} MB in total.',
      },
      {
        q: 'Can I fetch my mail with an email client?',
        a: 'Yes, via POP3 or POP3S, for example with Thunderbird: server {host}, port 995 (SSL/TLS) or 110 (STARTTLS), the user name is the full email address, plus your mailbox password.',
      },
      {
        q: 'Why was my mailbox locked?',
        a: 'Mailboxes that violate the terms of use (see imprint) are locked. Locked mailboxes cannot be reactivated.',
      },
      {
        q: 'What is the blacklist {zone}?',
        a: 'A DNS blacklist (DNSBL) of IPv4 addresses of mail servers that have been identified as sources of spam. On the “Blacklist” page you can check whether an address is listed.',
      },
    ],
  },
  blacklist: {
    title: 'Blacklist check',
    intro:
      '{zone} is a DNS blacklist (DNSBL) of IPv4 addresses of mail servers that have been identified as sources of spam. Check here whether an address is listed.',
    label: 'IPv4 address',
    submit: 'Check',
    busy: 'Checking …',
    clean: '{ip} is not listed.',
    listed: '{ip} is listed.',
    unavailable: 'The blacklist is currently unavailable. Please try again later.',
    codes: 'Return code',
    reason: 'Reason',
    usageTitle: 'Usage in SpamAssassin',
    usage: 'For example, add to /etc/spamassassin/local.cf:',
    removalForm: 'Request removal',
    removalIntro: 'If an address is listed, you can request its removal right after checking it above.',
    removalHint: 'Briefly describe why the address is listed by mistake or what has been fixed (e.g. open relay closed, compromised account disabled).',
    name: 'Name or company',
    notice: 'Explanation',
    noticeMin: 'At least {min} characters.',
    captcha: 'Security code',
    captchaHint: 'Please enter the characters shown in the image (not case-sensitive).',
    captchaReload: 'New image',
    captchaAlt: 'Security code as image',
    removalSubmit: 'Send request',
    removalBusy: 'Sending …',
    removalCreated: 'Your request for {ip} has been received and will be processed.',
    removalUpdated: 'There is already an open request for {ip}. Your message has been added to it.',
    downloadTitle: 'DNSBL Check for Windows',
    downloadText: 'With the DNSBL Check program you can check IP addresses against the blacklist directly on Windows.',
    downloadLink: 'Download DNSBLCheck.zip',
    removalTitle: 'Removal',
    removal: 'Is your address listed by mistake? Write to us with the IP address and a short explanation; the contact details are in the {link}.',
  },
  imprint: {
    title: 'Imprint',
    provider: 'Information according to § 5 DDG (German Digital Services Act)',
    contact: 'Contact',
    disclaimerTitle: 'Disclaimer',
    contentTitle: 'Liability for content',
    content:
      'The content of these pages has been created with the utmost care. No liability is accepted for the accuracy, completeness or timeliness of the content or for any resulting damage.',
    linksTitle: 'Liability for links',
    links:
      'This website contains links to external third-party websites over whose content we have no influence. The respective provider or operator is always responsible for this content.',
    termsTitle: 'Terms of use',
    terms: [
      'By creating an address you accept these terms of use.',
      'All applicable national and international laws must be observed when using the service.',
      'The service is free of charge. There is no entitlement to availability or usability; the service may be discontinued at any time without notice.',
      'Mailboxes are locked or deleted in the event of serious violations.',
      'You should back up received messages yourself. Claims for damages are excluded.',
      'These terms of use may change.',
    ],
    logsTitle: 'Log data',
    logs: 'When the website is accessed, date and time, browser, referrer and IP address are stored in the server log files.',
  },
  errors: {
    generic: 'This action is currently not possible.',
    unavailable: 'The service is currently unavailable.',
    rate_limited: 'Too many attempts. Please wait and try again later.',
    invalid_credentials: 'Email address or password incorrect.',
    account_locked: 'The mailbox is locked.',
    account_expired: 'The mailbox has expired.',
    address_taken: 'This address is already taken.',
    local_part_length: 'The address must be {min} to {max} characters long.',
    local_part_chars: 'Allowed are lowercase letters, digits, dot, hyphen and underscore (not at the start or end, no double dots).',
    local_part_reserved: 'This address is reserved.',
    weak_password: {
      too_short: 'The password must be at least {min} characters long.',
      too_long: 'The password must be at most {max} characters long.',
      invalid_chars: 'The password contains invalid characters.',
      whitespace: 'The password must not start or end with a space.',
      contains_address: 'The password must not contain the email address.',
    },
    wrong_password: 'The current password is incorrect.',
    same_password: 'The new password must differ from the current one.',
    not_expired: 'The mailbox is still active and does not need to be reactivated.',
    invalid_days: 'Please choose one of the offered lifetimes.',
    extend_limit: 'The mailbox can currently be extended by at most {max} days.',
    extend_max: 'The mailbox already has the maximum lifetime.',
    not_listed: '{ip} is not listed and does not need to be removed.',
    dnsbl_unavailable: 'The blacklist is currently unavailable. Please try again later.',
    captcha_invalid: 'The security code is wrong or has expired. Please enter the new code.',
    invalid_ip: 'Please enter a valid IPv4 address.',
    session_expired: 'Please log in again.',
  },
};

export default en;
